import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { SettingsSection } from '@frontend/settings-ui';
import { sentry } from '@frontend/tracking';
import { Button, useAlert, useFormField } from '@frontend/design-system';
import { CancellationPolicyText } from './CancellationPolicyText';

export const CancellationPolicy = ({ locationId }: { locationId: string }) => {
  const { t } = useTranslation('schedule-settings');
  const alert = useAlert();

  const { data, isLoading } = SchedulerV3.Queries.useListLocationSettingsQuery({
    request: { locationIds: [locationId] },
    options: { enabled: !!locationId },
  });

  const locationSettings = data?.locationsSettings?.[locationId];

  const { mutateAsync, isLoading: isSaving } = SchedulerV3.Mutations.useUpdateLocationSettingsMutation();

  const cancellationPolicyText = useFormField(
    {
      type: 'text',
      value: locationSettings?.cancellationPolicyText ?? '',
      placeholder: t('Enter your cancellation policy here...'),
    },
    [locationSettings?.cancellationPolicyText]
  );

  const handleSave = async () => {
    try {
      await mutateAsync({ locationId, cancellationPolicyText: cancellationPolicyText.value });
      alert.success(t('Cancellation policy saved successfully'));
    } catch (error) {
      sentry.error({
        error,
        team: 'schedule',
        topic: 'scheduling',
        addContext: {
          name: 'cancellation-policy-save-failure',
          context: {
            locationId,
            currentLength: cancellationPolicyText.value.length,
            previousLength: locationSettings?.cancellationPolicyText?.length ?? 0,
            isClearingPolicy: cancellationPolicyText.value.length === 0,
          },
        },
      });
      alert.error(t('Failed to save cancellation policy'));
    }
  };

  const isSaveDisabled =
    isLoading || isSaving || cancellationPolicyText.value === locationSettings?.cancellationPolicyText;

  return (
    <SettingsSection>
      <CancellationPolicyText fieldProps={cancellationPolicyText} value={cancellationPolicyText.value} />
      <section css={{ marginTop: `var(--fabric-space-gap-lg)`, display: `flex`, justifyContent: `flex-end` }}>
        <Button
          variant='secondary'
          onClick={() =>
            cancellationPolicyText.onChange({
              value: locationSettings?.cancellationPolicyText ?? '',
              name: 'cancellationPolicyText',
            })
          }
          disabled={isLoading || isSaving || cancellationPolicyText.value === locationSettings?.cancellationPolicyText}
        >
          {t('Reset')}
        </Button>
        <div css={{ width: `var(--fabric-space-gap-sm)` }} />
        <Button variant='primary' onClick={handleSave} disabled={isSaveDisabled}>
          {t('Save')}
        </Button>
      </section>
    </SettingsSection>
  );
};
